import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const TrackingInputForm = ({ onSubmit, onBulkSubmit, recentEntries }) => {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrierType, setCarrierType] = useState('auto');
  const [validationStatus, setValidationStatus] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const inputRef = useRef(null);

  const carrierOptions = [
    { value: 'auto', label: 'Auto-Detect Carrier' },
    { value: 'royalmail', label: 'Royal Mail (2 letters + 9 digits + GB)' },
    { value: 'dpd', label: 'DPD (14-16 digits)' }
  ];

  useEffect(() => {
    if (inputRef?.current && !bulkMode) {
      inputRef?.current?.focus();
    }
  }, [bulkMode]);

  const validateFormat = (number) => {
    const patterns = {
      royalmail: /^[A-Z]{2}\d{9}GB$/,
      dpd: /^\d{14,16}$/
    };

    if (carrierType === 'auto') {
      for (const [carrier, pattern] of Object.entries(patterns)) {
        if (pattern?.test(number)) {
          return { valid: true, carrier: carrier?.toUpperCase() };
        }
      }
      return { valid: false, carrier: null };
    }

    return {
      valid: patterns?.[carrierType]?.test(number) || false,
      carrier: carrierType?.toUpperCase()
    };
  };

  const handleInputChange = (e) => {
    const value = e?.target?.value?.trim()?.toUpperCase();
    setTrackingNumber(value);

    if (value?.length > 0) {
      const validation = validateFormat(value);
      setValidationStatus(validation);
    } else {
      setValidationStatus(null);
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (trackingNumber && validationStatus?.valid) {
      onSubmit({
        trackingNumber,
        carrier: validationStatus?.carrier,
        timestamp: new Date()?.toISOString()
      });
      setTrackingNumber('');
      setValidationStatus(null);
    }
  };

  const handleBulkPaste = () => {
    const numbers = bulkText?.split(/[\n,]+/)?.map(n => n?.trim()?.toUpperCase())?.filter(n => n?.length > 0);

    if (numbers?.length > 0 && numbers?.length <= 100) {
      onBulkSubmit(numbers);
      setBulkText('');
      setBulkMode(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSubmit(e);
    } else if (e?.key === 'Escape') {
      setTrackingNumber('');
      setValidationStatus(null);
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 border border-border p-24 md:p-32 lg:p-40">
      <div className="flex items-center justify-between mb-24 md:mb-32">
        <div className="flex items-center gap-12">
          <div className="w-[44px] h-[44px] md:w-[52px] md:h-[52px] bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Package" size={24} color="var(--color-primary)" strokeWidth={2} />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-lg md:text-xl lg:text-2xl text-foreground">
              {bulkMode ? 'Bulk Entry Mode' : 'Tracking Number Entry'}
            </h2>
            <p className="font-body text-xs md:text-sm text-muted-foreground">
              {bulkMode ? 'Paste up to 100 tracking numbers' : 'Enter tracking number for validation'}
            </p>
          </div>
        </div>
        <Button
          variant={bulkMode ? 'outline' : 'ghost'}
          size="sm"
          iconName={bulkMode ? 'X' : 'Upload'}
          onClick={() => setBulkMode(!bulkMode)}
        >
          {bulkMode ? 'Single' : 'Bulk'}
        </Button>
      </div>
      {!bulkMode ? (
        <form onSubmit={handleSubmit} className="space-y-24">
          <Select
            label="Carrier Type"
            options={carrierOptions}
            value={carrierType}
            onChange={setCarrierType}
            className="mb-24"
          />

          <div className="relative">
            <Input
              ref={inputRef}
              label="Tracking Number"
              type="text"
              placeholder="Enter or scan tracking number"
              value={trackingNumber}
              onChange={handleInputChange}
              onKeyDown={handleKeyPress}
              required
              className="font-mono text-base md:text-lg"
            />
            {validationStatus && (
              <div className="absolute right-12 top-[42px] flex items-center gap-8">
                {validationStatus?.valid ? (
                  <>
                    <span className="font-caption text-xs text-success font-medium">
                      {validationStatus?.carrier}
                    </span>
                    <Icon name="CheckCircle2" size={20} color="var(--color-success)" strokeWidth={2} />
                  </>
                ) : (
                  <>
                    <span className="font-caption text-xs text-error font-medium">
                      Invalid Format
                    </span>
                    <Icon name="XCircle" size={20} color="var(--color-error)" strokeWidth={2} />
                  </>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-12">
            <Button
              type="submit"
              variant="default"
              size="lg"
              iconName="Check"
              disabled={!trackingNumber || !validationStatus?.valid}
              fullWidth
            >
              Validate & Submit
            </Button>
            <Button
              type="button"
              variant="outline"
              size="lg"
              iconName="RotateCcw"
              onClick={() => {
                setTrackingNumber('');
                setValidationStatus(null);
              }}
            >
              Clear
            </Button>
          </div>

          <div className="bg-muted rounded-lg p-18 space-y-12">
            <div className="flex items-center gap-12">
              <Icon name="Keyboard" size={18} color="var(--color-muted-foreground)" strokeWidth={2} />
              <span className="font-caption text-xs md:text-sm text-muted-foreground font-medium">
                Keyboard Shortcuts
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 text-xs md:text-sm">
              <div className="flex items-center gap-8">
                <kbd className="px-8 py-4 bg-background rounded border border-border font-mono text-xs">Enter</kbd>
                <span className="text-muted-foreground">Submit</span>
              </div>
              <div className="flex items-center gap-8">
                <kbd className="px-8 py-4 bg-background rounded border border-border font-mono text-xs">Esc</kbd>
                <span className="text-muted-foreground">Clear</span>
              </div>
              <div className="flex items-center gap-8">
                <kbd className="px-8 py-4 bg-background rounded border border-border font-mono text-xs">Ctrl+V</kbd>
                <span className="text-muted-foreground">Bulk Paste</span>
              </div>
              <div className="flex items-center gap-8">
                <kbd className="px-8 py-4 bg-background rounded border border-border font-mono text-xs">Ctrl+I</kbd>
                <span className="text-muted-foreground">Bulk Import</span>
              </div>
            </div>
          </div>
        </form>
      ) : (
        <div className="space-y-24">
          <div>
            <label className="font-body font-medium text-sm md:text-base text-foreground mb-12 block">
              Paste Tracking Numbers
            </label>
            <textarea
              value={bulkText}
              onChange={(e) => setBulkText(e?.target?.value)}
              placeholder="Paste tracking numbers (comma or line-separated, max 100)"
              className="w-full h-[240px] md:h-[320px] px-18 py-18 bg-background border border-border rounded-lg font-mono text-sm md:text-base text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <p className="font-caption text-xs md:text-sm text-muted-foreground mt-8">
              {bulkText?.split(/[\n,]+/)?.filter(n => n?.trim()?.length > 0)?.length} / 100 numbers
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-12">
            <Button
              variant="default"
              size="lg"
              iconName="Upload"
              onClick={handleBulkPaste}
              disabled={bulkText?.trim()?.length === 0}
              fullWidth
            >
              Process Bulk Entry
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="X"
              onClick={() => setBulkText('')}
            >
              Clear
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackingInputForm;